import type {
    RagThreadAnswerOutcome,
    RagThreadAttachment,
    RagThreadFeedbackRating,
    RagThreadMessageDTO,
    RagThreadMessageOrigin,
    RagThreadMessageStatus,
} from 'crqlar-react-common/dist/models/ai/ragThread.model';

export interface AssistantSource {
    title: string;
    url: string;
    excerpt?: string;
    confidence?: number;
    documentId?: string;
    chunkId?: string;
}

export type AssistantFeedbackRating = RagThreadFeedbackRating;

export interface AssistantFeedback {
    rating: AssistantFeedbackRating;
    explanation?: string;
}

export interface AssistantMessage {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    seq?: number;
    origin?: RagThreadMessageOrigin;
    status?: RagThreadMessageStatus;
    /** AI answers only: NOT_FOUND means the Support Center had nothing, which is what offers the team. */
    answerOutcome?: RagThreadAnswerOutcome;
    insertDate?: Date;
    updateDate?: Date;
    attachments?: RagThreadAttachment[];
    sources?: AssistantSource[];
    isError?: boolean;
    isLocalOnly?: boolean;
    feedback?: AssistantFeedback;
}

export const createAssistantMessageId = () =>
    `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const mapSource = (
    source: NonNullable<RagThreadMessageDTO['sources']>[number],
    index: number,
): AssistantSource => ({
    title: source.title || `Chunk ${index + 1}`,
    url: source.url || '',
    excerpt: source.excerpt,
    confidence: source.confidence,
    documentId: source.documentId,
    chunkId: source.chunkId,
});

export const mapThreadMessage = (message: RagThreadMessageDTO): AssistantMessage => ({
    id: message.id || createAssistantMessageId(),
    role: message.author === 'USER' ? 'user' : 'assistant',
    content: message.text || '',
    seq: message.seq,
    origin: message.origin,
    status: message.status,
    answerOutcome: message.answerOutcome,
    insertDate: message.insertDate,
    updateDate: message.updateDate,
    attachments: message.attachments,
    sources: message.sources
        ?.filter(source => !!source.title || !!source.excerpt)
        .map(mapSource),
    feedback: message.feedback?.rating
        ? {
              rating: message.feedback.rating,
              explanation: message.feedback.explanation,
          }
        : undefined,
});
